<template>
  <a-drawer :open="visible" :title="node ? `${node.name} · 健康详情` : '健康详情'" width="560" @close="emit('update:visible', false)">
    <template v-if="node">
      <div class="head">
        <a-tag :color="levelMeta(node.level).color">{{ levelMeta(node.level).label }}</a-tag>
        <span class="muted">心跳 {{ formatAge(node.heartbeat_age_seconds) }} · {{ node.agent_version || '版本未知' }}</span>
      </div>

      <ul v-if="node.reasons.length" class="reasons">
        <li v-for="r in node.reasons" :key="r.text" :class="r.severity">{{ r.text }}</li>
      </ul>
      <p v-else class="muted">一切正常。</p>

      <a-descriptions :column="2" size="small" bordered class="facts">
        <a-descriptions-item label="7 天在线率">{{ formatUptime(node.uptime_7d) }}</a-descriptions-item>
        <a-descriptions-item label="掉线 / 重连">{{ node.offline_7d }} / {{ node.reconnects_7d }}</a-descriptions-item>
        <a-descriptions-item label="活跃 proxy">{{ node.active_proxies }}（缺失 {{ node.missing_children }}）</a-descriptions-item>
        <a-descriptions-item label="实时连接">{{ node.live_connections }}</a-descriptions-item>
        <a-descriptions-item label="proxy 进程死亡">{{ node.proxy_died_7d }} 次</a-descriptions-item>
        <a-descriptions-item label="agent 版本变化">{{ node.version_changes_7d }} 次</a-descriptions-item>
      </a-descriptions>

      <h4>最近 7 天（每格 1 小时）</h4>
      <HealthStrip :cells="node.strip" :generated-at="generatedAt" />
      <div class="legend">
        <span><i style="background:#52c41a" />正常</span>
        <span><i style="background:#fadb14" />CPU 高</span>
        <span><i style="background:#faad14" />部分离线/缺进程</span>
        <span><i style="background:#ff4d4f" />大部分离线</span>
        <span><i style="background:#e5e7eb" />无记录</span>
      </div>

      <h4>事件</h4>
      <a-spin :spinning="loading">
        <a-empty v-if="!loading && !detail?.events.length" description="最近 7 天没有记录到事件" />
        <a-timeline v-else>
          <a-timeline-item v-for="(e, i) in detail?.events ?? []" :key="i" :color="eventColor(e.kind)">
            <b>{{ eventLabel(e.kind) }}</b>
            <span v-if="e.detail" class="muted"> · {{ e.detail }}</span>
            <div class="muted">{{ formatTime(e.at) }}</div>
          </a-timeline-item>
        </a-timeline>
      </a-spin>
      <a-alert v-if="loadError" type="error" show-icon :message="loadError" />
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { getNodeHealth } from '../api/health';
import type { HealthNode, HealthNodeDetail } from '../types';
import HealthStrip from './HealthStrip.vue';
import { formatAge, formatUptime, levelMeta } from './healthPresentation';

const props = defineProps<{ visible: boolean; node: HealthNode | null; generatedAt: string }>();
const emit = defineEmits<{ (e: 'update:visible', value: boolean): void }>();

const detail = ref<HealthNodeDetail | null>(null);
const loading = ref(false);
const loadError = ref('');

const EVENTS: Record<string, { label: string; color: string }> = {
  online: { label: '上线', color: 'green' },
  offline: { label: '掉线', color: 'red' },
  reconnect: { label: '重连（agent 重启或网络抖动）', color: 'orange' },
  version_change: { label: 'agent 版本变化', color: 'blue' },
  proxy_died: { label: 'proxy 进程死亡', color: 'orange' },
};
const eventLabel = (kind: string) => EVENTS[kind]?.label ?? kind;
const eventColor = (kind: string) => EVENTS[kind]?.color ?? 'gray';
const formatTime = (iso: string) => new Date(iso).toLocaleString('zh-CN', { hour12: false });

watch(
  () => [props.visible, props.node?.id] as const,
  async ([visible, id]) => {
    if (!visible || id == null) return;
    loading.value = true;
    loadError.value = '';
    detail.value = null;
    try {
      detail.value = (await getNodeHealth(id)).data;
    } catch (e: any) {
      loadError.value = e?.response?.data?.error?.message || e?.message || '加载失败';
    } finally {
      loading.value = false;
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.muted { color: #9ca3af; font-size: 12px; }
.reasons { margin: 0 0 12px; padding-left: 18px; }
.reasons .critical { color: #cf1322; }
.reasons .attention { color: #d46b08; }
.reasons .info { color: #6b7280; }
.facts { margin-bottom: 16px; }
h4 { margin: 18px 0 8px; }
.legend { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 6px; font-size: 12px; color: #6b7280; }
.legend i { display: inline-block; width: 10px; height: 10px; margin-right: 4px; vertical-align: -1px; }
</style>
